/**
 * ─────────────────────────────────────────────────────────────
 * SITE CONFIG — Primeur Condorcet (Grenoble).
 *
 * Identité réelle du commerce : primeur / alimentation générale
 * au 20 rue Condorcet à Grenoble, également point relais Mondial Relay.
 * Les horaires proviennent des annuaires (PagesJaunes, Mappy) et restent
 * modifiables par le gérant via le contenu éditable.
 * ─────────────────────────────────────────────────────────────
 */
import type { OpeningHour } from '@/lib/types';

export interface SiteConfig {
  name: string;
  legalName: string;
  tagline: string;
  description: string;
  /** Absolute URL of the deployed site (overridden by NEXT_PUBLIC_SITE_URL). */
  url: string;
  locale: string;

  contact: {
    email: string;
    phone: string;
    address: string;
    city: string;
    postalCode: string;
    country: string;
    geo?: { lat: number; lng: number };
  };

  social: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };

  /** Brand palette — fed straight into the MUI theme. */
  brand: {
    primary: string;
    secondary: string;
    mode: 'light' | 'dark';
  };

  /** Default opening hours. The owner can override these live via the
   *  `hours` content slot; use resolveHours() to read the effective value. */
  openingHours: OpeningHour[];

  /** Appointment booking defaults. */
  booking: {
    slotMinutes: number;
    horizonDays: number;
    services: string[];
  };

  /** Currency for product prices (ISO 4217). */
  currency: string;
}

export const siteConfig: SiteConfig = {
  name: 'Primeur Condorcet',
  legalName: 'PRIMEUR CONDORCET',
  tagline: 'Votre primeur de quartier, rue Condorcet à Grenoble',
  description:
    'Primeur Condorcet, votre marchand de fruits et légumes frais au 20 rue Condorcet à Grenoble. Produits de saison, conseil de quartier et point relais Mondial Relay.',
  url: process.env.NEXT_PUBLIC_SITE_URL || 'https://www.primeurcondorcet.fr',
  locale: 'fr_FR',

  contact: {
    email: '',
    phone: '+33 9 87 76 30 40',
    address: '20 Rue Condorcet',
    city: 'Grenoble',
    postalCode: '38000',
    country: 'FR',
    geo: { lat: 45.1857583, lng: 5.7200181 },
  },

  social: {},

  brand: {
    // Vert feuillage de marché — l’identité du primeur.
    primary: '#2f6b3d',
    // Carotte / terracotta — l’accent chaleureux des étals.
    secondary: '#d9822b',
    mode: 'light',
  },

  openingHours: [
    { day: 1, open: '09:00', close: '13:00' },
    { day: 1, open: '15:30', close: '19:30' },
    { day: 2, open: '09:00', close: '13:00' },
    { day: 2, open: '15:30', close: '19:30' },
    { day: 3, open: '09:00', close: '13:00' },
    { day: 3, open: '15:30', close: '19:30' },
    { day: 4, open: '09:00', close: '13:00' },
    { day: 4, open: '15:30', close: '19:30' },
    { day: 5, open: '09:00', close: '13:00' },
    { day: 5, open: '15:30', close: '19:30' },
    { day: 6, open: '09:00', close: '13:00' },
    { day: 6, open: '15:30', close: '19:00' },
  ],

  booking: {
    slotMinutes: 30,
    horizonDays: 30,
    services: ['Conseil fruits & légumes', 'Commande de paniers'],
  },

  currency: 'EUR',
};

export default siteConfig;
